// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createHook,
	createStateHook,
	createStore,
	type BoundActions,
	type HookFunction,
	type HookStateFunction,
	type OverrideContainerComponent,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import { actions, type Actions } from './insightGlobalConfigurationStoreActions.tsx';
import type {
	ActionApi,
	CDMState,
	ContainerProps,
	DataManagerState,
	IconType,
	NewIconType,
	ReferenceType,
	State,
	StatusType,
	StatusTypeResponse,
} from './ActionApi.types.tsx';
import { sortReferenceTypes, sortStatusTypes } from './sortReferenceTypes.tsx';
import type { RequestBodyType as UpdateIconBody } from './updateIcon.tsx';
import type { RequestBodyType as UpdateReferenceTypeBody } from './updateReferenceType.tsx';
import type { RequestBodyType as UpdateStatusTypeBody } from './updateStatusType.tsx';

export const initialState: State = {
	loading: false,
	error: undefined,
	config: undefined,
	referenceTypes: {
		data: undefined,
		createLoading: false,
		createError: undefined,
		deleteLoading: false,
		deleteError: undefined,
		updateLoading: false,
		updateError: undefined,
	},
	statusTypes: {
		data: undefined,
		createLoading: false,
		createError: undefined,
		deleteLoading: false,
		deleteError: undefined,
		updateLoading: false,
		updateError: undefined,
	},
	icons: {
		data: undefined,
		deleteLoading: false,
		deleteError: undefined,
		addLoading: false,
		addError: undefined,
		updateLoading: false,
		updateError: undefined,
	},
	dataManager: {
		rolesData: undefined,
		rolesError: undefined,
		rolesLoading: false,
		rolesUpdateLoading: false,
		rolesUpdateError: undefined,
		tokensLoading: false,
		tokensData: undefined,
		tokensError: undefined,
		tokenRevokeLoading: false,
		tokenRevokeError: undefined,
		isProvisioned: undefined,
		provisioningLoading: false,
		provisioningError: undefined,
		provisioningUpdateLoading: false,
		provisioningUpdateError: undefined,
		myRoles: undefined,
		myRolesError: undefined,
		myRolesLoading: false,
		isDataManagerAvailable: undefined,
		isDataManagerAvailableLoading: false,
	},
	cdm: {
		instantiations: undefined,
		tasks: [],
	},
};

const Store = createStore({
	initialState,
	actions,
});

export const useInsightGlobalConfig: HookFunction<
	State,
	BoundActions<
		State,
		{
			readonly addIcon: (newIcon: NewIconType) => ActionApi;
			readonly createReferenceType: (data: {
				name: string;
				description: string;
				color: string;
			}) => ActionApi;
			readonly createStatusType: (data: {
				name: string;
				description: string;
				category: string;
			}) => ActionApi;
			readonly deleteIcon: (icon: IconType) => ActionApi;
			readonly deleteReferenceType: (referenceType: ReferenceType) => ActionApi;
			readonly deleteStatusType: (statusType: StatusType) => ActionApi;
			readonly fetchCdm: () => ActionApi;
			readonly fetchDataManagerTokens: () => ActionApi;
			readonly fetchGlobalConfig: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchMyDataManagerRoles: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchPrincipalsForRoles: (roleIds: string[]) => ActionApi;
			readonly fetchProvisioningStatus: (shouldRefetch?: boolean) => ActionApi;
			readonly installCdm: () => ActionApi;
			readonly setIcon: (updatedIcon: IconType) => ActionApi;
			readonly setReferenceType: (updatedReferenceType: ReferenceType) => ActionApi;
			readonly setStatusType: (updatedStatusType: StatusTypeResponse) => ActionApi;
			readonly updateDataManagerRole: ({
				principalIds,
				roleId,
			}: {
				principalIds: string[];
				roleId: string;
			}) => ActionApi;
			readonly updateIcon: ({ iconId, updatedProperties }: UpdateIconBody) => ActionApi;
			readonly updateProvisioningStatus: ({ isEnabled }: { isEnabled: boolean }) => ActionApi;
			readonly updateReferenceType: ({
				referenceTypeId,
				updatedProperties,
			}: UpdateReferenceTypeBody) => ActionApi;
			readonly updateStatusType: ({
				statusTypeId,
				updatedProperties,
			}: UpdateStatusTypeBody) => ActionApi;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state,
});

export const useInsightReferenceTypes: HookFunction<
	{
		data: ReferenceType[] | undefined;
		createLoading: boolean;
		createError: undefined | Error;
		deleteLoading: boolean;
		deleteError: undefined | Error;
		updateLoading: boolean;
		updateError: undefined | Error;
	},
	BoundActions<
		State,
		{
			readonly addIcon: (newIcon: NewIconType) => ActionApi;
			readonly createReferenceType: (data: {
				name: string;
				description: string;
				color: string;
			}) => ActionApi;
			readonly createStatusType: (data: {
				name: string;
				description: string;
				category: string;
			}) => ActionApi;
			readonly deleteIcon: (icon: IconType) => ActionApi;
			readonly deleteReferenceType: (referenceType: ReferenceType) => ActionApi;
			readonly deleteStatusType: (statusType: StatusType) => ActionApi;
			readonly fetchCdm: () => ActionApi;
			readonly fetchDataManagerTokens: () => ActionApi;
			readonly fetchGlobalConfig: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchMyDataManagerRoles: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchPrincipalsForRoles: (roleIds: string[]) => ActionApi;
			readonly fetchProvisioningStatus: (shouldRefetch?: boolean) => ActionApi;
			readonly installCdm: () => ActionApi;
			readonly setIcon: (updatedIcon: IconType) => ActionApi;
			readonly setReferenceType: (updatedReferenceType: ReferenceType) => ActionApi;
			readonly setStatusType: (updatedStatusType: StatusTypeResponse) => ActionApi;
			readonly updateDataManagerRole: ({
				principalIds,
				roleId,
			}: {
				principalIds: string[];
				roleId: string;
			}) => ActionApi;
			readonly updateIcon: ({ iconId, updatedProperties }: UpdateIconBody) => ActionApi;
			readonly updateProvisioningStatus: ({ isEnabled }: { isEnabled: boolean }) => ActionApi;
			readonly updateReferenceType: ({
				referenceTypeId,
				updatedProperties,
			}: UpdateReferenceTypeBody) => ActionApi;
			readonly updateStatusType: ({
				statusTypeId,
				updatedProperties,
			}: UpdateStatusTypeBody) => ActionApi;
		}
	>,
	void
> = createHook(Store, {
	selector: ({ referenceTypes }) => ({
		...referenceTypes,
		data: referenceTypes.data ? sortReferenceTypes(referenceTypes.data) : undefined,
	}),
});

export const useInsightStatusTypes: HookFunction<
	{
		data: StatusTypeResponse[] | undefined;
		createLoading: boolean;
		createError: undefined | Error;
		deleteLoading: boolean;
		deleteError: undefined | Error;
		updateLoading: boolean;
		updateError: undefined | Error;
	},
	BoundActions<
		State,
		{
			readonly addIcon: (newIcon: NewIconType) => ActionApi;
			readonly createReferenceType: (data: {
				name: string;
				description: string;
				color: string;
			}) => ActionApi;
			readonly createStatusType: (data: {
				name: string;
				description: string;
				category: string;
			}) => ActionApi;
			readonly deleteIcon: (icon: IconType) => ActionApi;
			readonly deleteReferenceType: (referenceType: ReferenceType) => ActionApi;
			readonly deleteStatusType: (statusType: StatusType) => ActionApi;
			readonly fetchCdm: () => ActionApi;
			readonly fetchDataManagerTokens: () => ActionApi;
			readonly fetchGlobalConfig: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchMyDataManagerRoles: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchPrincipalsForRoles: (roleIds: string[]) => ActionApi;
			readonly fetchProvisioningStatus: (shouldRefetch?: boolean) => ActionApi;
			readonly installCdm: () => ActionApi;
			readonly setIcon: (updatedIcon: IconType) => ActionApi;
			readonly setReferenceType: (updatedReferenceType: ReferenceType) => ActionApi;
			readonly setStatusType: (updatedStatusType: StatusTypeResponse) => ActionApi;
			readonly updateDataManagerRole: ({
				principalIds,
				roleId,
			}: {
				principalIds: string[];
				roleId: string;
			}) => ActionApi;
			readonly updateIcon: ({ iconId, updatedProperties }: UpdateIconBody) => ActionApi;
			readonly updateProvisioningStatus: ({ isEnabled }: { isEnabled: boolean }) => ActionApi;
			readonly updateReferenceType: ({
				referenceTypeId,
				updatedProperties,
			}: UpdateReferenceTypeBody) => ActionApi;
			readonly updateStatusType: ({
				statusTypeId,
				updatedProperties,
			}: UpdateStatusTypeBody) => ActionApi;
		}
	>,
	void
> = createHook(Store, {
	selector: ({ statusTypes }) => ({
		...statusTypes,
		data: statusTypes.data ? sortStatusTypes(statusTypes.data) : undefined,
	}),
});

export const useInsightIcons: HookFunction<
	{
		data: undefined | IconType[];
		deleteLoading: boolean;
		deleteError: undefined | Error;
		updateLoading: boolean;
		updateError: undefined | Error;
		addLoading: boolean;
		addError: undefined | Error;
	},
	BoundActions<
		State,
		{
			readonly addIcon: (newIcon: NewIconType) => ActionApi;
			readonly createReferenceType: (data: {
				name: string;
				description: string;
				color: string;
			}) => ActionApi;
			readonly createStatusType: (data: {
				name: string;
				description: string;
				category: string;
			}) => ActionApi;
			readonly deleteIcon: (icon: IconType) => ActionApi;
			readonly deleteReferenceType: (referenceType: ReferenceType) => ActionApi;
			readonly deleteStatusType: (statusType: StatusType) => ActionApi;
			readonly fetchCdm: () => ActionApi;
			readonly fetchDataManagerTokens: () => ActionApi;
			readonly fetchGlobalConfig: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchMyDataManagerRoles: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchPrincipalsForRoles: (roleIds: string[]) => ActionApi;
			readonly fetchProvisioningStatus: (shouldRefetch?: boolean) => ActionApi;
			readonly installCdm: () => ActionApi;
			readonly setIcon: (updatedIcon: IconType) => ActionApi;
			readonly setReferenceType: (updatedReferenceType: ReferenceType) => ActionApi;
			readonly setStatusType: (updatedStatusType: StatusTypeResponse) => ActionApi;
			readonly updateDataManagerRole: ({
				principalIds,
				roleId,
			}: {
				principalIds: string[];
				roleId: string;
			}) => ActionApi;
			readonly updateIcon: ({ iconId, updatedProperties }: UpdateIconBody) => ActionApi;
			readonly updateProvisioningStatus: ({ isEnabled }: { isEnabled: boolean }) => ActionApi;
			readonly updateReferenceType: ({
				referenceTypeId,
				updatedProperties,
			}: UpdateReferenceTypeBody) => ActionApi;
			readonly updateStatusType: ({
				statusTypeId,
				updatedProperties,
			}: UpdateStatusTypeBody) => ActionApi;
		}
	>,
	void
> = createHook(Store, {
	selector: ({ icons }) => icons,
});

export const useDataManager: HookFunction<
	DataManagerState,
	BoundActions<
		State,
		{
			readonly addIcon: (newIcon: NewIconType) => ActionApi;
			readonly createReferenceType: (data: {
				name: string;
				description: string;
				color: string;
			}) => ActionApi;
			readonly createStatusType: (data: {
				name: string;
				description: string;
				category: string;
			}) => ActionApi;
			readonly deleteIcon: (icon: IconType) => ActionApi;
			readonly deleteReferenceType: (referenceType: ReferenceType) => ActionApi;
			readonly deleteStatusType: (statusType: StatusType) => ActionApi;
			readonly fetchCdm: () => ActionApi;
			readonly fetchDataManagerTokens: () => ActionApi;
			readonly fetchGlobalConfig: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchMyDataManagerRoles: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchPrincipalsForRoles: (roleIds: string[]) => ActionApi;
			readonly fetchProvisioningStatus: (shouldRefetch?: boolean) => ActionApi;
			readonly installCdm: () => ActionApi;
			readonly setIcon: (updatedIcon: IconType) => ActionApi;
			readonly setReferenceType: (updatedReferenceType: ReferenceType) => ActionApi;
			readonly setStatusType: (updatedStatusType: StatusTypeResponse) => ActionApi;
			readonly updateDataManagerRole: ({
				principalIds,
				roleId,
			}: {
				principalIds: string[];
				roleId: string;
			}) => ActionApi;
			readonly updateIcon: ({ iconId, updatedProperties }: UpdateIconBody) => ActionApi;
			readonly updateProvisioningStatus: ({ isEnabled }: { isEnabled: boolean }) => ActionApi;
			readonly updateReferenceType: ({
				referenceTypeId,
				updatedProperties,
			}: UpdateReferenceTypeBody) => ActionApi;
			readonly updateStatusType: ({
				statusTypeId,
				updatedProperties,
			}: UpdateStatusTypeBody) => ActionApi;
		}
	>,
	void
> = createHook(Store, {
	selector: ({ dataManager }) => dataManager,
});

export const useCommonDataModel: HookFunction<
	CDMState,
	BoundActions<
		State,
		{
			readonly addIcon: (newIcon: NewIconType) => ActionApi;
			readonly createReferenceType: (data: {
				name: string;
				description: string;
				color: string;
			}) => ActionApi;
			readonly createStatusType: (data: {
				name: string;
				description: string;
				category: string;
			}) => ActionApi;
			readonly deleteIcon: (icon: IconType) => ActionApi;
			readonly deleteReferenceType: (referenceType: ReferenceType) => ActionApi;
			readonly deleteStatusType: (statusType: StatusType) => ActionApi;
			readonly fetchCdm: () => ActionApi;
			readonly fetchDataManagerTokens: () => ActionApi;
			readonly fetchGlobalConfig: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchMyDataManagerRoles: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchPrincipalsForRoles: (roleIds: string[]) => ActionApi;
			readonly fetchProvisioningStatus: (shouldRefetch?: boolean) => ActionApi;
			readonly installCdm: () => ActionApi;
			readonly setIcon: (updatedIcon: IconType) => ActionApi;
			readonly setReferenceType: (updatedReferenceType: ReferenceType) => ActionApi;
			readonly setStatusType: (updatedStatusType: StatusTypeResponse) => ActionApi;
			readonly updateDataManagerRole: ({
				principalIds,
				roleId,
			}: {
				principalIds: string[];
				roleId: string;
			}) => ActionApi;
			readonly updateIcon: ({ iconId, updatedProperties }: UpdateIconBody) => ActionApi;
			readonly updateProvisioningStatus: ({ isEnabled }: { isEnabled: boolean }) => ActionApi;
			readonly updateReferenceType: ({
				referenceTypeId,
				updatedProperties,
			}: UpdateReferenceTypeBody) => ActionApi;
			readonly updateStatusType: ({
				statusTypeId,
				updatedProperties,
			}: UpdateStatusTypeBody) => ActionApi;
		}
	>,
	void
> = createHook(Store, {
	selector: ({ cdm }) => cdm,
});

export const useNamedInsightIcons: HookStateFunction<
	Record<string, IconType>,
	void
> = createStateHook(Store, {
	selector: ({ icons }) => {
		const map: Record<string, IconType> = {};
		(icons.data ?? []).forEach((icon) => {
			map[icon.name] = icon;
		});
		return map;
	},
});

export const isRestrictedSchema = (state: State, schemaId: string): boolean => {
	const restrictedObjectSchemaIds = state.config?.restrictedObjectSchemaIds ?? [];
	return restrictedObjectSchemaIds.includes(parseInt(schemaId, 10));
};

export const useIsRestrictedSchema: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			readonly addIcon: (newIcon: NewIconType) => ActionApi;
			readonly createReferenceType: (data: {
				name: string;
				description: string;
				color: string;
			}) => ActionApi;
			readonly createStatusType: (data: {
				name: string;
				description: string;
				category: string;
			}) => ActionApi;
			readonly deleteIcon: (icon: IconType) => ActionApi;
			readonly deleteReferenceType: (referenceType: ReferenceType) => ActionApi;
			readonly deleteStatusType: (statusType: StatusType) => ActionApi;
			readonly fetchCdm: () => ActionApi;
			readonly fetchDataManagerTokens: () => ActionApi;
			readonly fetchGlobalConfig: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchMyDataManagerRoles: (shouldRefetch?: boolean) => ActionApi;
			readonly fetchPrincipalsForRoles: (roleIds: string[]) => ActionApi;
			readonly fetchProvisioningStatus: (shouldRefetch?: boolean) => ActionApi;
			readonly installCdm: () => ActionApi;
			readonly setIcon: (updatedIcon: IconType) => ActionApi;
			readonly setReferenceType: (updatedReferenceType: ReferenceType) => ActionApi;
			readonly setStatusType: (updatedStatusType: StatusTypeResponse) => ActionApi;
			readonly updateDataManagerRole: ({
				principalIds,
				roleId,
			}: {
				principalIds: string[];
				roleId: string;
			}) => ActionApi;
			readonly updateIcon: ({ iconId, updatedProperties }: UpdateIconBody) => ActionApi;
			readonly updateProvisioningStatus: ({ isEnabled }: { isEnabled: boolean }) => ActionApi;
			readonly updateReferenceType: ({
				referenceTypeId,
				updatedProperties,
			}: UpdateReferenceTypeBody) => ActionApi;
			readonly updateStatusType: ({
				statusTypeId,
				updatedProperties,
			}: UpdateStatusTypeBody) => ActionApi;
		}
	>,
	string
> = createHook(Store, {
	selector: isRestrictedSchema,
});

const autoFetchGlobalConfig =
	(): ActionApi =>
	({ dispatch, getState }, { disableOnInit }) => {
		const { loading, error, config } = getState();
		if (fg('data-manager-units-onboarding')) {
			dispatch(actions.fetchIsDataManagerAvailable());
		}

		if (!disableOnInit && !loading && !error && !config) {
			dispatch(actions.fetchGlobalConfig());
			if (fg('assets_cdm_ui_flow')) {
				dispatch(actions.fetchCdm());
			}
			if (fg('assets_as_an_app_v2')) {
				dispatch(actions.fetchProvisioningStatus());
				dispatch(actions.fetchMyDataManagerRoles());
			}
		}
	};

export const InsightGlobalConfigContainer: OverrideContainerComponent<ContainerProps> =
	createContainer<State, Actions, ContainerProps>(Store, {
		onInit: autoFetchGlobalConfig,
	});
